import { Alert, Box, Button, Divider, Group, Modal, NumberInput, Paper, Select, Stack, Text, Textarea } from "@mantine/core"
import { useForm } from "@mantine/form"
import { CheckIcon } from "@phosphor-icons/react"
import { yupResolver } from "mantine-form-yup-resolver"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { IPullSheetLineShortage, SHORTAGE_REASON_CODES } from "../../apis/picking-shortage"
import type { IPullSheetLine } from "../../models/PullSheetLine"
import { ConfirmNote, nextStepToast } from "@dehub/theme"
import { formatQuantity } from "../../utils/format-quantity"
import { translateBackendError } from "../delivery/translate-error"
import { decisionFor, useShortageMutations, useShortageThresholds } from "./hooks"

type FormValues = {
  lineId: string
  shortQuantity: number | string
  reasonCode: string
  reasonText: string
  photoUrlsRaw: string
}

type Props = Readonly<{
  opened: boolean
  onClose: () => void
  pullSheetId: string
  lines: IPullSheetLine[]
  shortages: IPullSheetLineShortage[]
}>

const parseUrls = (raw: string) =>
  raw
    .split("\n")
    .map((u) => u.trim())
    .filter(Boolean)

export function ReportShortageDialog({ opened, onClose, pullSheetId, lines, shortages }: Props) {
  const { t } = useTranslation(["delivery", "common"])
  const { submitting, error, clearError, reportShortage } = useShortageMutations()
  const thresholds = useShortageThresholds()
  const [confirmReplace, setConfirmReplace] = useState(false)

  const lineById = useMemo(() => new Map(lines.map((l) => [l.id as string, l])), [lines])

  const schema = useMemo(
    () =>
      yup.object({
        lineId: yup.string().required(t("shortage.dialog.validation.line")),
        shortQuantity: yup
          .number()
          .typeError(t("shortage.dialog.validation.quantity"))
          .moreThan(0, t("shortage.dialog.validation.quantity"))
          .required(t("shortage.dialog.validation.quantity"))
          .test("max", t("shortage.dialog.validation.quantityMax"), function (val) {
            const line = lineById.get((this.parent as FormValues).lineId)
            return !line || !val || Number(val) <= Number(line.quantity)
          }),
        reasonCode: yup.string().required(t("shortage.dialog.validation.reason")),
        reasonText: yup.string().nullable().optional(),
        photoUrlsRaw: yup
          .string()
          .nullable()
          .optional()
          .test("urls", t("shortage.dialog.validation.url"), (val) => parseUrls(val ?? "").every((u) => /^https?:\/\/\S+$/.test(u)))
          .test("damaged-needs-photo", t("shortage.dialog.validation.photo"), function (val) {
            if ((this.parent as FormValues).reasonCode !== "DAMAGED") return true
            return parseUrls(val ?? "").length >= 1
          }),
      }),
    [t, lineById],
  )

  const form = useForm<FormValues>({
    initialValues: { lineId: "", shortQuantity: 1, reasonCode: "", reasonText: "", photoUrlsRaw: "" },
    validate: yupResolver(schema) as any,
  })

  const selectedLine = lineById.get(form.values.lineId)
  const lineOptions = lines.map((l) => ({
    value: l.id as string,
    label: [[l.name, l.optionValue].filter(Boolean).join(", "), l.skuCode].filter(Boolean).join(" · "),
  }))
  const reasonOptions = SHORTAGE_REASON_CODES.map((code) => ({ value: code, label: t(`shortage.reason.${code}`) }))

  // What the decision becomes after this report: Σ short / Σ loaded, this line's old value replaced.
  const preview = useMemo(() => {
    const entered = Number(form.values.shortQuantity)
    if (!selectedLine || !(entered > 0)) return null
    const total = lines.reduce((a, l) => a + Number(l.quantity ?? 0), 0)
    const others = shortages.filter((s) => s.pullSheetLineId !== selectedLine.id)
    const short = others.reduce((a, s) => a + Number(s.shortQuantity ?? 0), 0) + entered
    const percent = total > 0 ? Math.min(100, (short / total) * 100) : 0
    return { short, total, percent, decision: decisionFor(percent, others.length + 1, thresholds) }
  }, [form.values.shortQuantity, selectedLine, lines, shortages, thresholds])

  const handleClose = () => {
    form.reset()
    clearError()
    setConfirmReplace(false)
    onClose()
  }

  const send = async (values: FormValues, replace: boolean) => {
    const photoUrls = parseUrls(values.photoUrlsRaw)
    try {
      await reportShortage(pullSheetId, values.lineId, {
        shortQuantity: Number(values.shortQuantity),
        reasonCode: values.reasonCode,
        reasonText: values.reasonText || null,
        photoUrls: photoUrls.length > 0 ? photoUrls : undefined,
        replace,
      })
      nextStepToast("order.supplier-shipping-list.toast.shortage-report", { fallback: { title: t("shortage.dialog.success") } })
      handleClose()
    } catch (e) {
      // 409: the line already has a report. Ask before overwriting it, then resend with replace: true.
      if ((e as { alreadyReported?: boolean }).alreadyReported) setConfirmReplace(true)
    }
  }

  const handleSubmit = form.onSubmit((values) => send(values, confirmReplace))

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={650} fz="var(--nw-fz-drawer)">
          {t("shortage.dialog.title")}
        </Text>
      }
      size={600}
      radius="lg"
      centered
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Stack gap="sm">
          {error && !confirmReplace && (
            <Alert color="gray" variant="light">
              {translateBackendError(error)}
            </Alert>
          )}
          {confirmReplace && (
            <Alert color="yellow" variant="light" title={t("shortage.dialog.replaceTitle")}>
              {t("shortage.dialog.replaceText")}
              <ConfirmNote helpKey="order.supplier-shipping-list.confirm.shortage-replace" mt={8} />
            </Alert>
          )}

          <Select
            label={t("shortage.dialog.line")}
            placeholder={t("shortage.dialog.linePlaceholder")}
            data={lineOptions}
            searchable
            nothingFoundMessage={t("shortage.nothingFound")}
            required
            {...form.getInputProps("lineId")}
            onChange={(v) => {
              form.setFieldValue("lineId", v ?? "")
              setConfirmReplace(false)
            }}
          />

          <Group grow align="flex-start">
            <NumberInput
              label={t("shortage.dialog.quantity")}
              description={selectedLine ? t("shortage.dialog.quantityHint", { qty: formatQuantity(selectedLine.quantity) }) : undefined}
              min={0}
              max={selectedLine ? Number(selectedLine.quantity) : undefined}
              decimalScale={4}
              required
              {...form.getInputProps("shortQuantity")}
            />
            <Select
              label={t("shortage.dialog.reasonLabel")}
              placeholder={t("shortage.dialog.reasonPlaceholder")}
              data={reasonOptions}
              required
              {...form.getInputProps("reasonCode")}
            />
          </Group>

          <Textarea label={t("shortage.dialog.note")} placeholder={t("shortage.dialog.notePlaceholder")} rows={3} {...form.getInputProps("reasonText")} />

          <Textarea
            label={form.values.reasonCode === "DAMAGED" ? t("shortage.dialog.photosRequired") : t("shortage.dialog.photos")}
            description={form.values.reasonCode === "DAMAGED" ? t("shortage.dialog.damagedHint") : undefined}
            placeholder={t("shortage.dialog.photosPlaceholder")}
            rows={3}
            {...form.getInputProps("photoUrlsRaw")}
          />

          <Paper bg="gray.0" radius="md" p="sm">
            <Text fz={14} fw={700} mb={4}>
              {t("shortage.decision.thresholds")}
            </Text>
            {(
              [
                ["AUTO_SPLIT", t("shortage.decision.below", { n: thresholds.review })],
                ["COORDINATOR_REVIEW", t("shortage.decision.between", { a: thresholds.review, b: thresholds.escalation })],
                ["MANAGER_ESCALATION", t("shortage.decision.above", { n: thresholds.escalation })],
              ] as const
            ).map(([code, band], i) => {
              const hit = preview?.decision === code
              return (
                <Box key={code}>
                  {i > 0 && <Divider color="gray.2" />}
                  <Group justify="space-between" py={6} wrap="nowrap">
                    <Text fz={14}>{band}</Text>
                    <Group gap={6} wrap="nowrap">
                      {hit && <CheckIcon size={14} />}
                      <Text fz={14} fw={hit ? 700 : 400}>
                        {t(`shortage.decision.${code}`)}
                      </Text>
                    </Group>
                  </Group>
                </Box>
              )
            })}
            {preview && (
              <Text fz={14} fw={600} mt={6}>
                {t("shortage.dialog.preview", {
                  short: formatQuantity(preview.short),
                  total: formatQuantity(preview.total),
                  percent: preview.percent.toFixed(1),
                  decision: t(`shortage.decision.${preview.decision}`),
                })}
              </Text>
            )}
          </Paper>

          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={handleClose} disabled={submitting}>
              {t("shortage.dialog.cancel")}
            </Button>
            <Button type="submit" color="dark" loading={submitting} leftSection={confirmReplace ? <CheckIcon size={16} /> : undefined}>
              {confirmReplace ? t("shortage.dialog.replace") : t("shortage.dialog.submit")}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
